import placeholders from "@pub/placeholders";
import {
  ResponsiveImage,
  SupportedCrops,
  useCustomClassContext,
} from "@sphtech/dmg-design-system";
import { getGeniusLink } from "@src/app/utils/trackers/skimlinks";
import cx from "classnames";
import { Disclaimer } from "packages/design-system/src/components/Element/Typography";
import { SubHeading3 } from "packages/design-system/src/components/Element/Typography/Heading/Heading";

import {
  isCUEImageElement,
  ResolvedColumnImageElement,
  ResolvedCUEElement,
  resolveImageCrop,
} from "../elements";
import styles from "../Storyline.module.css";
import { Annotation } from "./Annotation/Annotation";

const isResolvedColumnInfo = (
  item?: ResolvedCUEElement,
): item is ResolvedColumnImageElement =>
  !!item && item.type === "column_images_item_info";

export function ColumnImage({
  element: { children },
  bodyMaxWidth,
  imageCrop = "portrait",
}: {
  element: ResolvedCUEElement;
  bodyMaxWidth: number;
  imageCrop?: SupportedCrops;
}) {
  const customClass = useCustomClassContext();
  return (
    <div className={styles.columnImageContainer}>
      {children?.map((item, index) => {
        if (!item.children) return;
        const image = item.children.find((item) => item.type === "image");
        const crop =
          isCUEImageElement(image) && resolveImageCrop(image, imageCrop);
        if (!crop) {
          return null;
        }

        const caption = image.additionalFields.caption && (
          <p
            className={cx(
              styles.imageCaption,
              customClass?.figureCaptions?.imageCaption,
            )}
          >
            <Annotation element={image.additionalFields.caption} />
          </p>
        );
        const credit = image.additionalFields.credit && (
          <Disclaimer>
            {image.additionalFields.credit.annotations?.length ? (
              <Annotation element={image.additionalFields.credit} />
            ) : (
              crop.credit
            )}
          </Disclaimer>
        );

        const imageElement = (
          <ResponsiveImage
            key={index}
            src={crop.src}
            alt={crop.alt}
            srcWidth={crop.srcWidth}
            srcHeight={crop.srcHeight}
            displayWidth={bodyMaxWidth / 2}
            loading="lazy"
            errorSrc={placeholders.portrait.src}
          />
        );

        const link = item.children.find(
          (item) => item.type === "column_images_item_info",
        );
        const href =
          isResolvedColumnInfo(link) && link.additionalFields.targetUrl?.value;

        const title = isResolvedColumnInfo(link) &&
          link.additionalFields.title?.value && (
            <SubHeading3>{link.additionalFields.title.value}</SubHeading3>
          );

        if (href) {
          return (
            <a
              key={index}
              className={cx(styles.column, styles.columnLink)}
              href={getGeniusLink(href)}
              target="_blank"
              rel="noreferrer"
            >
              {title}
              {imageElement}
              <div className={styles.captionContainer}>
                {caption}
                {credit}
              </div>
            </a>
          );
        }

        return (
          <div key={index} className={styles.column}>
            {title}
            {imageElement}
            <div className={styles.captionContainer}>
              {caption}
              {credit}
            </div>
          </div>
        );
      })}
    </div>
  );
}
